import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTimer } from 'react-timer-hook';
import { RECAPTCHA_SITE_KEY } from '@env';
import ReCAPTCHA from 'react-google-recaptcha';
import { Platform, StyleSheet, TextInput, View } from 'react-native';
import I18n from '../../../components/shared/i18n/I18n.component';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import ButtonV2 from '../../../components/shared/Buttons/ButtonV2.container';
import colors from '../../../config/colors';
import { OtpTarget } from '../SignInV2.utils';

export const CODE_LENGTH = 6;
/** cooldown before a new code may be requested */
const RESEND_SECONDS = 30;
/** wrong codes before the captcha is required */
const MAX_FAILED_ATTEMPTS = 3;
/** placeholder for an empty slot, so a cleared middle box doesn't shift the rest */
const BLANK = ' ';

const padCode = (code: string) => (code + BLANK.repeat(CODE_LENGTH)).slice(0, CODE_LENGTH);

export const isCodeComplete = (code: string) => !padCode(code).includes(BLANK);

const toMmSs = (totalSeconds: number) => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2,'0',)}`;
};

const expiryFromNow = (seconds: number) => new Date(Date.now() + seconds * 1000);

interface OtpStepProps {
	otpTarget: OtpTarget | null;
	code: string;
	setCode: (val: string) => void;
	onSubmit: () => void;
	onResend: () => void;
	onBack: () => void;
	/** bumped every time a code is sent - restarts the resend cooldown */
	resendNonce: number;
	/** too many wrong codes - submit stays off until a resend or a fresh flow */
	locked: boolean;
	errorKey: string | null;
	loading: boolean;
	failedAttempts: number;
}

export const OtpStep = ({
	otpTarget,
	code,
	setCode,
	onSubmit,
	onResend,
	onBack,
	resendNonce,
	errorKey,
	loading,
	locked,
	failedAttempts,
}: OtpStepProps) => {
	const inputs = useRef<Array<TextInput | null>>([]);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const digits = padCode(code);
	const complete = isCodeComplete(code);
	// always the latest code, so two fast keystrokes don't build on a stale copy
	const codeRef = useRef(digits);
	codeRef.current = digits;

	// update the ref before the state, so the next keystroke already sees it
	const commit = (next: string) => {
		codeRef.current = next;
		setCode(next);
	};
	const isPhone = otpTarget?.type === 'phone';
	const captchaRef = useRef<any>();
	const [captchaVerified, setCaptchaVerified] = useState(false);
	const showCaptcha =
		Platform.OS === 'web' && failedAttempts >= MAX_FAILED_ATTEMPTS;
	const captchaBlocked = showCaptcha && !captchaVerified;

	// memoized: a fresh Date every render would keep restarting the timer
	const initialExpiry = useMemo(() => expiryFromNow(RESEND_SECONDS), []);
	const { minutes, seconds, restart } = useTimer({
		expiryTimestamp: initialExpiry,
		autoStart: true,
	});
	const secondsLeft = minutes * 60 + seconds;
	const resendDisabled = secondsLeft > 0 || loading;

	// a new code was sent -> restart the cooldown
	useEffect(() => {
		restart(expiryFromNow(RESEND_SECONDS));
	}, [resendNonce]);

	// the last box just got filled -> submit without waiting for the button
	useEffect(() => {
		complete && !loading && !locked && !captchaBlocked && onSubmit();
	}, [code]);


	useEffect(() => {
		setCaptchaVerified(false);
		captchaRef.current?.reset();
	}, [failedAttempts]);

	const onChangeDigit = (index: number, value: string) => {
		const arr = codeRef.current.split('');
		const typed = value.replace(/\D/g, '');

		// nothing left -> the user emptied this box
		if (!typed) {
			arr[index] = BLANK;
			commit(arr.join(''));
			return;
		}

		// box was already filled, so we got the old digit plus the new one.
		// the caret decides the order, so drop the old one wherever it sits.
		const current = arr[index] === BLANK ? '' : arr[index];
		const incoming =
			current && typed.length === 2 ? typed.replace(current, '') : typed;

		// one char lands here, a paste spills into the boxes after it
		incoming.split('').forEach((char, offset) => {
			if (index + offset < CODE_LENGTH) {
				arr[index + offset] = char;
			}
		});
		commit(arr.join(''));
		// jump past whatever we just wrote
		inputs.current[
			Math.min(index + incoming.length, CODE_LENGTH - 1)
		]?.focus();
	};

	const onKeyPressDigit = (index: number, e: any) => {
		if (e.nativeEvent.key !== 'Backspace') {
			return;
		}
		const arr = codeRef.current.split('');
		// backspace on an already-empty box steps back and clears the previous one
		if (arr[index] === BLANK && index > 0) {
			arr[index - 1] = BLANK;
			commit(arr.join(''));
			inputs.current[index - 1]?.focus();
		}
	};

	return (
		<>
			<I18n
				size={17}
				font='medium'
				color='primaryDarkBlue'
				style={styles.title}
			>
				{isPhone ? 'signInV2.otp.titlePhone' : 'signInV2.otp.titleMail'}
			</I18n>
			{!!otpTarget?.masked && (
				<I18n
					size={14}
					color='subDarkBlue'
					style={styles.subtitle}
					target={otpTarget.masked}
				>
					{isPhone
						? 'signInV2.otp.subtitlePhone'
						: 'signInV2.otp.subtitleMail'}
				</I18n>
			)}
			<I18n size={14} color='subDarkBlue' style={styles.enterCode}>
				signInV2.otp.enterCode
			</I18n>
			<View style={styles.codeRow}>
				{Array.from({ length: CODE_LENGTH }).map((_, index) => (
					<TextInput
						key={index}
						ref={ref => {
							inputs.current[index] = ref;
						}}
						style={[
							styles.codeBox,
							focusedIndex === index ? styles.codeBoxFocused : {},
							errorKey ? styles.codeBoxError : {},
						]}
						underlineColorAndroid='transparent'
						value={digits[index] === BLANK ? '' : digits[index]}
						onChangeText={value => onChangeDigit(index, value)}
						onKeyPress={e => onKeyPressDigit(index, e)}
						onFocus={() => setFocusedIndex(index)}
						// guarded: the next box's onFocus can land first, and an
						// unguarded clear would wipe the highlight it just set
						onBlur={() =>
							setFocusedIndex(curr =>
								curr === index ? null : curr,
							)
						}
						keyboardType='number-pad'
						spellCheck={false}
						autoCorrect={false}
						selectTextOnFocus
						// let the platform drop the whole code into the first box;
						// onChangeDigit spreads it across the rest
						textContentType={
							index === 0 && Platform.OS === 'ios'
								? 'oneTimeCode'
								: 'none'
						}
						{...Platform.select({
							web:
								index === 0
									? { autoComplete: 'one-time-code' }
									: {},
						})}
					/>
				))}
			</View>
			{!!errorKey && (
				<View style={styles.errorRow}>
					<ImageIcon
						name={'alertCircle'}
						color={colors.failure}
						width={15}
						height={15}
						style={{ marginLeft: 4 }}
					/>
					<I18n color='failure' weight='normal' size={12}>
						{errorKey}
					</I18n>
				</View>
			)}
			<View style={styles.resendRow}>
				<I18n size={14} color='subDarkBlue'>
					signInV2.otp.noCode
				</I18n>
				<View style={styles.resendAction}>
					{secondsLeft > 0 && (
						<I18n
							size={14}
							weight='bold'
							color='titlePrimary'
							style={styles.timer}
						>
							{`${toMmSs(secondsLeft)}  |`}
						</I18n>
					)}
					<ButtonV2
						preset='link'
						onPress={onResend}
						disabled={resendDisabled}
						overrideStyle={styles.resendButton}
					>
						<I18n size={14} weight='400' color='titlePrimary'>
							signInV2.otp.resend
						</I18n>
					</ButtonV2>
				</View>
			</View>
			{showCaptcha && (
				<View style={styles.captchaRow}>
					<ReCAPTCHA
						ref={captchaRef}
						onChange={() => setCaptchaVerified(true)}
						onExpired={() => setCaptchaVerified(false)}
						sitekey={RECAPTCHA_SITE_KEY}
					/>
				</View>
			)}
			<ButtonV2
				preset='primary'
				size='medium'
				disabled={!complete || loading || locked || captchaBlocked}
				onPress={onSubmit}
				overrideStyle={{ width: '100%' }}
			>
				<I18n size={15} weight='normal' color='white'>
					signInV2.otp.submit
				</I18n>
			</ButtonV2>
			<View style={styles.backRow}>
				<ButtonV2
					preset='link'
					size='medium'
					disabled={loading}
					onPress={onBack}
					overrideStyle={{ width: '100%' }}
				>
					<I18n size={14} weight='400' color='titlePrimary'>
						signInV2.otp.back
					</I18n>
				</ButtonV2>
			</View>
		</>
	);
};

const styles = StyleSheet.create({
	title: {
		textAlign: 'center',
		marginBottom: 32,
		letterSpacing: 0.15,
	},
	subtitle: {
		textAlign: 'right',
		marginBottom: 12,
	},
	enterCode: {
		textAlign: 'right',
		marginBottom: 12,
	},
	codeRow: {
		flexDirection: 'row',
	},	
	codeBox: {
		flex: 1,
		marginHorizontal: 4,
		maxWidth: 56,
		height: 54,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: colors.clockGreyText,
		backgroundColor: colors.white,
		textAlign: 'center',
		fontSize: 16,
		fontWeight: '500',
		color: colors.primaryDarkBlue,
		...Platform.select({
			web: {
				outlineWidth: 0,
				outlineStyle: 'none',
			},
		}),
	},
	codeBoxFocused: {
		borderColor: colors.titlePrimary,
	},
	codeBoxError: {
		borderColor: colors.failure,
	},
	errorRow: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		marginTop: 12,
	},
	resendRow: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginTop: 32,
		marginBottom: 24,
	},
	resendAction: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	// strip ButtonV2's block sizing so the link sits tight next to the timer
	resendButton: {
		minWidth: 0,
		paddingHorizontal: 4,
		width: 110,
	},
	timer: {
		fontVariant: ['tabular-nums'],
		marginRight: 4,
		marginLeft: 4,
	},
	captchaRow: {
		alignItems: 'center',
		marginBottom: 16,
	},
	backRow: {
		marginTop: 12,
	},
});
